import { randomUuid } from "@talkie/agentic-app-transport";
import { z } from "zod/v4";
import { initialPatients, patientResults, catalogs } from "./seed.js";

// v2: order forms left the clinical record (they live in the UI workspace),
// and receipts became a bounded list of hashed fingerprints. v1 snapshots were
// never shipped; they are ignored rather than migrated (PRD NG5).
export const STORAGE_KEY = "careline-ehr-agentic-v2";
const LEGACY_STORAGE_KEYS = ["careline-ehr-agentic-v1"];
/** Retry window: the most recent receipts kept for request-ID replay. */
export const MAX_RECEIPTS = 500;
export const patients = initialPatients();
export { catalogs };
const text = z.string().max(8000);
const short = z.string().trim().min(1).max(200);
export const orderType = z.enum(["Laboratory", "Imaging", "Diagnostic test", "Referral", "Prescription"]);
export const noteSchema = z
  .object({
    hpi: text.describe("History of present illness."),
    exam: text.describe("Physical exam findings."),
    assessment: text.describe("Assessment and plan."),
    instructions: text.describe("Instructions for the patient, shown on the after-visit summary."),
    followup: z
      .enum(["3 months", "2 weeks", "1 month", "6 months", "As needed"])
      .describe("When the patient should come back."),
  })
  .strict();
export const pharmacies = ["Oak Street Pharmacy · 124 Oak St", "Central Pharmacy · 220 Main St"] as const;
export const orderFields = z
  .object({
    type: orderType.describe("Kind of order. Prescriptions take strength, directions, quantity, refills and pharmacy; every other kind takes priority and timing."),
    name: short.describe(
      "What is ordered. For every kind except Prescription, exactly one of the names list_orderables returns for this type, e.g. \"Lipid panel\".",
    ),
    diagnosis: short.describe(
      "The diagnosis this order is for: one of the problems on this patient's chart, exactly as get_chart_summary lists it.",
    ),
    notes: text.default("").describe("Free-text notes for the order. Optional."),
    strength: short.optional().describe("Prescriptions only, and required for them: the strength, e.g. \"500 mg\"."),
    directions: short
      .optional()
      .describe("Prescriptions only, and required for them: how to take it, e.g. \"Take 1 tablet twice daily\"."),
    quantity: z
      .string()
      .regex(/^[1-9]\d{0,2}$/)
      .optional()
      .describe("Prescriptions only, and required for them: how many to dispense, 1 to 999, as a string."),
    refills: z
      .string()
      .regex(/^(?:[0-9]|1[01])$/)
      .optional()
      .describe("Prescriptions only, and required for them: number of refills, 0 to 11, as a string."),
    pharmacy: z.enum(pharmacies).optional().describe("Prescriptions only, and required for them: the pharmacy to send to."),
    priority: z
      .enum(["Routine", "Urgent"])
      .optional()
      .describe("Every kind except Prescription, and required for them: how urgent the order is."),
    timing: z
      .enum(["Within 1 week", "Today", "Before next visit"])
      .optional()
      .describe("Every kind except Prescription, and required for them: when it should happen."),
  })
  .strict();
export const orderSchema = orderFields.extend({
  id: short,
  revision: z.number().int().positive(),
  status: z.enum(["Draft", "Sent (simulated)", "Ordered (simulated)"]),
  signed_at_ms: z.number().int().nonnegative().optional(),
});
export const encounterSchema = z
  .object({
    id: short,
    revision: z.number().int().positive(),
    medReviewed: z.boolean(),
    allergyReviewed: z.boolean(),
    historyReviewed: z.boolean(),
    resultReviewed: z.array(z.number().int().min(0).max(4)).max(5),
    note: noteSchema,
    saved: z.boolean(),
    diagnoses: z.array(short).max(20),
    orders: z.array(orderSchema).max(200),
    signed: z.boolean(),
    signed_at_ms: z.number().int().nonnegative().optional(),
  })
  .strict();
export type Encounter = z.infer<typeof encounterSchema>;
export type Order = z.infer<typeof orderSchema>;
export type OrderFields = z.infer<typeof orderFields>;
export type Note = z.infer<typeof noteSchema>;
export type Scope = {
  patient_id: string;
  encounter_id: string;
  expected_revision: number;
  request_id: string;
};
export const persistenceSchema = z.enum(["local", "session-only"]);
export const resultSchema = z
  .object({
    patient_id: short,
    encounter_id: short,
    revision: z.number().int().positive(),
    status: z.string(),
    simulated: z.literal(true),
    persistence: persistenceSchema,
    entity_id: short.optional(),
    order: orderSchema.optional(),
  })
  .strict();
export type Result = z.infer<typeof resultSchema>;
const receiptSchema = z
  .object({ request_id: short, fingerprint: z.string().max(64), result: resultSchema })
  .strict();
const snapshotSchema = z
  .object({
    schema_version: z.literal(2),
    visits: z.record(short, encounterSchema),
    receipts: z.array(receiptSchema).max(MAX_RECEIPTS),
  })
  .strict();
type Snapshot = z.infer<typeof snapshotSchema>;
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
export type StoreEvent = { action: string; patient_id: string; source: string; result: Result };
export type StoreOptions = {
  storage?: Storage;
  log?: (event: string, detail: object) => void;
  /** Encounter-completion blockers owned by the UI (e.g. an unfinished order form). */
  uiBlockers?: (patientId: string) => string[];
};
export class AppError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}
const fail = (code: string, message: string): never => {
  throw new AppError(code, message);
};
const clone = <T>(value: T): T => structuredClone(value);
const canonical = (value: unknown): string =>
  JSON.stringify(value, (_key, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => a.localeCompare(b)))
      : v,
  );
/** Fixed-size fingerprint (two independent 32-bit FNV-1a lanes + length), so a receipt never stores the payload it guards. */
export function fingerprintOf(value: unknown): string {
  const input = canonical(value);
  let a = 0x811c9dc5,
    b = 0x01000193 ^ input.length;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x5bd1e995) >>> 0;
  }
  return `${a.toString(16).padStart(8, "0")}${b.toString(16).padStart(8, "0")}:${input.length}`;
}
const seed = (): Snapshot => ({
  schema_version: 2,
  receipts: [],
  visits: Object.fromEntries(
    patients.map((p) => [
      p.id,
      {
        id: `enc-${p.id}-20260928`,
        revision: 1,
        medReviewed: false,
        allergyReviewed: false,
        historyReviewed: false,
        resultReviewed: [],
        note: { hpi: "", exam: "", assessment: "", instructions: "", followup: "3 months" },
        saved: false,
        diagnoses: [],
        orders: [],
        signed: false,
      },
    ]),
  ),
});
/**
 * Whether a patient matches a free-text search, from `search_patients` or the directory's search box.
 * Every term must match the SAME patient's name or MRN, in any order, so "Maria Bennett, MRN 10428"
 * and "Bennett, Maria" both find her. The "MRN" label is not a term. No terms matches everyone.
 */
export function patientMatches(patient: { name: string; id: string }, query: string): boolean {
  const haystack = `${patient.name} ${patient.id}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((term) => term && term !== "mrn")
    .every((term) => haystack.includes(term));
}
export function getPatient(id: string) {
  return patients.find((p) => p.id === id) ?? fail("not_found", "Patient not found.");
}
export function resultsFor(id: string) {
  return patientResults(getPatient(id)).map((r, i) => ({ ...r, id: `result-${id}-${i}` }));
}
/**
 * The indexes of `ids` among the patient's results: one to five, none repeated, every one on this chart. The review
 * a clinician confirms (`review.ts`) and the write that records it share it, so both refuse the same list.
 */
export function resultIndexes(patientId: string, ids: unknown): number[] {
  const list = z.array(z.string()).min(1).max(5).safeParse(ids);
  if (!list.success || new Set(list.data).size !== list.data.length)
    fail("invalid_input", "Name one to five different results.");
  const all = resultsFor(patientId);
  return (list.data as string[]).map((id) => {
    const index = all.findIndex((r) => r.id === id);
    return index < 0 ? fail("not_found", "Result not found for this patient.") : index;
  });
}
export function blockers(v: Encounter, extra: string[] = []): string[] {
  return [
    !v.medReviewed && "Review the medication list.",
    !v.allergyReviewed && "Review allergies.",
    !v.historyReviewed && "Review clinical history.",
    !v.saved && "Save the encounter note.",
    !v.diagnoses.length && "Select at least one visit diagnosis.",
    v.orders.some((o) => o.status === "Draft") && "Sign or remove unsigned orders.",
    ...extra,
  ].filter((x): x is string => typeof x === "string");
}
export function medicationsFor(id: string, v: Encounter) {
  const rows = getPatient(id).meds.map((m, i) => ({
    id: `med-${id}-${i}`,
    name: m[0],
    strength: m[1],
    directions: m[2],
    date: m[3],
    order: undefined as Order | undefined,
  }));
  const key = (m: { name: string; strength: string }) =>
    `${m.name}|${m.strength}`.trim().replace(/\s+/g, " ").toLowerCase();
  for (const o of v.orders)
    if (o.type === "Prescription" && o.status === "Sent (simulated)") {
      const row = {
        id: o.id,
        name: o.name,
        strength: o.strength!,
        directions: o.directions!,
        date: new Date(o.signed_at_ms!).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        order: o,
      };
      const index = rows.findIndex((m) => key(m) === key(row));
      if (index < 0) rows.push(row);
      else rows[index] = row;
    }
  return rows;
}
const signedVisitDate = (ms: number) =>
  new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
export function historyFor(id: string, v: Encounter) {
  const p = getPatient(id);
  return [
    ...(v.signed
      ? [
          {
            id: v.id,
            date: signedVisitDate(v.signed_at_ms!),
            date_ms: v.signed_at_ms,
            summary: v.note.assessment,
            status: "Signed (simulated)",
          },
        ]
      : []),
    { id: `visit-${id}-0`, date: p.lastDate, summary: p.last, status: "Historical" },
    {
      id: `visit-${id}-1`,
      date: "Mar 12, 2026",
      summary:
        "Chronic conditions and medications reviewed. Office ECG completed. Follow-up plan documented.",
      status: "Historical",
    },
    {
      id: `visit-${id}-2`,
      date: "Nov 4, 2025",
      summary: "Office visit for persistent cough. Chest imaging completed and result reviewed.",
      status: "Historical",
    },
  ];
}
const RX_ONLY = ["strength", "directions", "quantity", "refills", "pharmacy"] as const;
const ORDER_ONLY = ["priority", "timing"] as const;
function validOrder(patientId: string, raw: unknown): OrderFields {
  const parsed = orderFields.safeParse(raw);
  if (!parsed.success) return fail("invalid_input", "Invalid order fields, quantity or refills.");
  const o = parsed.data;
  if (!getPatient(patientId).problems.includes(o.diagnosis))
    fail("invalid_input", "Choose a diagnosis from this patient chart.");
  if (o.type === "Prescription") {
    if (!o.strength || !o.directions || !o.quantity || !o.refills || !o.pharmacy)
      fail("invalid_input", "Complete prescription strength, directions, quantity, refills and pharmacy.");
    if (ORDER_ONLY.some((k) => o[k] !== undefined))
      fail(
        "invalid_input",
        "Priority and timing apply to diagnostic orders and referrals, not prescriptions.",
      );
  } else {
    if (!catalogs[o.type].includes(o.name) || !o.priority || !o.timing)
      fail("invalid_input", "Choose an available orderable, priority and timing.");
    if (RX_ONLY.some((k) => o[k] !== undefined))
      fail(
        "invalid_input",
        "Strength, directions, quantity, refills and pharmacy apply to prescriptions only.",
      );
  }
  return o;
}
function validateLoaded(parsed: Snapshot) {
  if (
    Object.keys(parsed.visits).sort().join(",") !==
    patients
      .map((p) => p.id)
      .sort()
      .join(",")
  )
    fail("invalid_snapshot", "Unknown patient set");
  if (new Set(parsed.receipts.map((r) => r.request_id)).size !== parsed.receipts.length)
    fail("invalid_snapshot", "Duplicate receipt");
  for (const p of patients) {
    const v = parsed.visits[p.id];
    if (
      v.id !== seed().visits[p.id].id ||
      new Set(v.orders.map((o) => o.id)).size !== v.orders.length ||
      v.orders.some((o) => !o.id.startsWith(`order-${p.id}-`))
    )
      fail("invalid_snapshot", "Invalid record ownership");
    for (const o of v.orders) {
      const { id, revision, status, signed_at_ms, ...fields } = o;
      validOrder(p.id, fields);
      if (status !== "Draft" && !signed_at_ms) fail("invalid_snapshot", "Missing signature");
    }
    if (
      v.diagnoses.some((d) => !p.problems.includes(d)) ||
      (v.signed && (!v.signed_at_ms || blockers(v).length))
    )
      fail("invalid_snapshot", "Invalid encounter");
  }
}
export function createStore(options: StoreOptions = {}) {
  const log = options.log ?? (() => {});
  const uiBlockers = options.uiBlockers ?? (() => []);
  // Writable only once a read has succeeded: a store whose read failed must
  // never overwrite what it could not read.
  let storage = options.storage;
  let state = seed();
  let persistence: "local" | "session-only" = storage ? "local" : "session-only";
  let issue = "";
  let blocked = false;
  const listeners = new Set<(event: StoreEvent) => void>();
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = snapshotSchema.parse(JSON.parse(raw));
      validateLoaded(parsed);
      state = parsed;
    }
  } catch (error) {
    if (error instanceof SyntaxError || error instanceof z.ZodError || error instanceof AppError) {
      blocked = true;
      issue = "Stored demo data is invalid. Reset the demo explicitly to recover.";
    } else {
      storage = undefined;
      persistence = "session-only";
      issue = "Browser storage is unavailable. Changes last only for this session.";
    }
  }
  log("ehr.storage_state", { phase: "load", persistence, blocked });
  function persist() {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(state));
      persistence = "local";
      issue = "";
    } catch {
      persistence = "session-only";
      issue = "Could not save to browser storage. Changes last only for this session.";
    }
    log("ehr.storage_state", { phase: "write", persistence, blocked });
  }
  function encounter(id: string, encounterId?: string): Encounter {
    if (blocked) fail("storage_unavailable", issue);
    getPatient(id);
    const v = state.visits[id];
    if (encounterId && v.id !== encounterId)
      fail("patient_mismatch", "Encounter does not belong to this patient.");
    return clone(v);
  }
  function order(v: Encounter, id: string) {
    return v.orders.find((o) => o.id === id) ?? fail("not_found", "Order not found in this encounter.");
  }
  /** The encounter a write at `scope` would change: the one `mutate` applies to, and the one a review is built from. */
  function writable(scope: Pick<Scope, "patient_id" | "encounter_id" | "expected_revision">): Encounter {
    const v = encounter(scope.patient_id, scope.encounter_id);
    if (v.signed) fail("encounter_signed", "Signed encounters are read only.");
    if (v.revision !== scope.expected_revision)
      fail("stale_revision", "The encounter changed. Read and review the latest revision before retrying.");
    return v;
  }
  // The two attestations a clinician makes, shared by the model-facing tools and the view's `record_review`.
  function setReview(v: Encounter, field: unknown, reviewed: unknown) {
    v[z.enum(["medReviewed", "allergyReviewed", "historyReviewed"]).parse(field)] = z.boolean().parse(reviewed);
  }
  function markResultsReviewed(v: Encounter, patientId: string, resultIds: unknown) {
    for (const index of resultIndexes(patientId, resultIds))
      if (!v.resultReviewed.includes(index)) v.resultReviewed.push(index);
  }
  function mutate(
    action: string,
    scope: Scope,
    payload: unknown,
    source: string,
    change: (v: Encounter) => Order | void,
  ): Result {
    const started = Date.now();
    try {
      short.parse(scope.request_id);
      if (blocked) fail("storage_unavailable", issue);
      const fingerprint = fingerprintOf({ action, scope, payload });
      const prior = state.receipts.find((r) => r.request_id === scope.request_id);
      // Replay is checked before the signed/stale checks so a legitimate retry recovers its original result.
      if (prior) {
        if (prior.fingerprint !== fingerprint)
          fail("request_conflict", "Request ID was already used with different content.");
        return { ...clone(prior.result), persistence };
      }
      const v = writable(scope);
      const changed = change(v);
      v.revision++;
      encounterSchema.parse(v);
      const result: Result = {
        patient_id: scope.patient_id,
        encounter_id: v.id,
        revision: v.revision,
        status: changed?.status ?? (v.signed ? "Signed (simulated)" : "Draft"),
        simulated: true,
        persistence,
        ...(changed ? { entity_id: changed.id, order: clone(changed) } : {}),
      };
      // The mutation and its receipt land in one snapshot; the oldest receipts age out of the retry window.
      state = {
        ...state,
        visits: { ...state.visits, [scope.patient_id]: v },
        receipts: [...state.receipts, { request_id: scope.request_id, fingerprint, result }].slice(
          -MAX_RECEIPTS,
        ),
      };
      persist();
      result.persistence = persistence;
      log("ehr.action_result", {
        action,
        request_id: scope.request_id,
        outcome: "success",
        duration_ms: Date.now() - started,
      });
      for (const listener of listeners)
        listener({ action, patient_id: scope.patient_id, source, result: clone(result) });
      return clone(result);
    } catch (error) {
      log("ehr.action_result", {
        action,
        request_id: scope.request_id,
        outcome: error instanceof AppError ? error.code : "invalid_input",
        duration_ms: Date.now() - started,
      });
      throw error;
    }
  }
  return {
    encounter,
    writable,
    order,
    blockers: (patientId: string) => blockers(encounter(patientId), uiBlockers(patientId)),
    status: () => ({ persistence, issue, blocked }),
    subscribe: (listener: (event: StoreEvent) => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    invalidate: () => {
      blocked = true;
      issue = "Demo data changed in another tab. Reload this page before editing.";
    },
    reset: () => {
      state = seed();
      blocked = false;
      issue = "";
      for (const key of LEGACY_STORAGE_KEYS) {
        try {
          storage?.removeItem(key);
        } catch {
          /* best effort */
        }
      }
      persist();
    },
    run(action: string, scope: Scope, payload: Record<string, unknown> = {}, source = "manual"): Result {
      return mutate(action, scope, payload, source, (v) => {
        switch (action) {
          case "update_note":
            v.note = noteSchema.parse({ ...v.note, ...noteSchema.partial().parse(payload.note) });
            v.saved = false;
            break;
          // The view's Save (own-app views FR-6): the COMPLETE note exactly as the clinician saw it, edits included,
          // with update_note's effect (the draft is accepted, not yet saved). A partial note is refused, not merged.
          case "apply_note_draft":
            v.note = noteSchema.parse(payload.note);
            v.saved = false;
            break;
          case "set_diagnoses": {
            const ds = z.array(short).max(20).parse(payload.diagnoses);
            if (ds.some((d) => !getPatient(scope.patient_id).problems.includes(d)))
              fail("invalid_input", "Diagnosis is not on this chart.");
            v.diagnoses = [...new Set(ds)];
            v.saved = false;
            break;
          }
          case "save_note":
            if (["hpi", "exam", "assessment", "instructions"].some((k) => !v.note[k as keyof Note].trim()))
              fail("incomplete_encounter", "Complete the required note sections before saving.");
            if (!v.diagnoses.length)
              fail("incomplete_encounter", "Select at least one visit diagnosis before saving.");
            v.saved = true;
            break;
          case "set_review_status":
            setReview(v, payload.field, payload.reviewed);
            break;
          case "review_result":
            markResultsReviewed(v, scope.patient_id, payload.result_ids);
            break;
          // The view's Confirm (own-app views FR-6): exactly one attestation, by results or by chart field.
          case "record_review": {
            const byResult = payload.result_ids !== undefined;
            const byField = payload.field !== undefined || payload.reviewed !== undefined;
            if (byResult === byField)
              fail("invalid_input", "Record exactly one review: result_ids, or a field with reviewed.");
            if (byResult) markResultsReviewed(v, scope.patient_id, payload.result_ids);
            else setReview(v, payload.field, payload.reviewed);
            break;
          }
          case "create_order":
          case "renew_prescription": {
            if (v.orders.length >= 200) fail("invalid_input", "Demo order limit reached.");
            let fields = payload.order;
            if (action === "renew_prescription") {
              const m =
                medicationsFor(scope.patient_id, v).find((m) => m.id === payload.medication_id) ??
                fail("not_found", "Medication not found.");
              fields = {
                type: "Prescription",
                name: m.name,
                strength: m.strength,
                directions: m.directions,
                quantity: m.order?.quantity ?? "30",
                refills: m.order?.refills ?? "0",
                pharmacy: m.order?.pharmacy ?? pharmacies[0],
                diagnosis: m.order?.diagnosis ?? getPatient(scope.patient_id).problems[0],
                notes: m.order?.notes ?? "",
              };
            }
            const o: Order = {
              ...validOrder(scope.patient_id, fields),
              id: `order-${scope.patient_id}-${randomUuid()}`,
              revision: 1,
              status: "Draft",
            };
            v.orders.push(o);
            return o;
          }
          case "update_order": {
            const o = order(v, String(payload.order_id));
            if (o.status !== "Draft") fail("invalid_input", "Signed orders are read only.");
            // Replace, never merge: fields the new order does not carry must not survive from the old one.
            const next: Order = {
              ...validOrder(scope.patient_id, payload.order),
              id: o.id,
              revision: o.revision + 1,
              status: "Draft",
            };
            v.orders = v.orders.map((x) => (x.id === o.id ? next : x));
            return next;
          }
          case "remove_order": {
            const o = order(v, String(payload.order_id));
            if (o.status !== "Draft") fail("invalid_input", "Only unsigned orders may be removed.");
            v.orders = v.orders.filter((x) => x.id !== o.id);
            break;
          }
          case "sign_order": {
            const o = order(v, String(payload.order_id));
            if (o.status !== "Draft") fail("invalid_input", "Order is already signed.");
            o.status = o.type === "Prescription" ? "Sent (simulated)" : "Ordered (simulated)";
            o.signed_at_ms = Date.now();
            o.revision++;
            return o;
          }
          case "sign_encounter": {
            const issues = blockers(v, uiBlockers(scope.patient_id));
            if (issues.length) fail("incomplete_encounter", issues.join(" "));
            v.signed = true;
            v.signed_at_ms = Date.now();
            break;
          }
          default:
            fail("invalid_input", "Unknown action.");
        }
      });
    },
  };
}
export type Store = ReturnType<typeof createStore>;
